import { postInboxOpenTiming } from "../../service/inbox";

/**
 * Click-to-page-drawn stopwatch for the inbox viewer.
 *
 * The list marks the click; the viewer starts a stopwatch when it begins
 * loading that document, marks each step, and finishes it when the first
 * page is drawn (or the load fails, or the attorney moves on). Every step
 * is milliseconds since the click, and the report also says how long the
 * browser's main thread was busy meanwhile — the server can't see either.
 *
 * Reporting is fire-and-forget: a failed report must never touch the UI.
 */

const CLICK_TTL_MS = 60_000;
const LONG_TASK_KEEP = 200;

const clicks = new Map();
const longTasks = [];
let longTaskObserver = null;
let lastHiddenAt = null;

const now = () => performance.now();

function watchMainThread() {
  if (longTaskObserver || typeof PerformanceObserver === "undefined") return;
  try {
    longTaskObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTasks.push({ start: entry.startTime, end: entry.startTime + entry.duration });
      }
      if (longTasks.length > LONG_TASK_KEEP) {
        longTasks.splice(0, longTasks.length - LONG_TASK_KEEP);
      }
    });
    longTaskObserver.observe({ type: "longtask", buffered: true });
  } catch {
    longTaskObserver = null;
  }
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) lastHiddenAt = now();
    });
  }
}

function busyBetween(from, to) {
  let ms = 0;
  let count = 0;
  for (const task of longTasks) {
    const overlap = Math.min(task.end, to) - Math.max(task.start, from);
    if (overlap > 0) {
      ms += overlap;
      count += 1;
    }
  }
  return { ms: Math.round(ms), count };
}

function connectionContext() {
  const connection = typeof navigator !== "undefined" ? navigator.connection : null;
  const context = {};
  if (connection) {
    if (connection.effectiveType) context.effective_type = connection.effectiveType;
    if (Number.isFinite(connection.rtt)) context.rtt_ms = connection.rtt;
    if (Number.isFinite(connection.downlink)) context.downlink_mbps = connection.downlink;
  }
  if (typeof navigator !== "undefined") {
    if (Number.isFinite(navigator.deviceMemory)) context.device_memory_gb = navigator.deviceMemory;
    if (Number.isFinite(navigator.hardwareConcurrency)) {
      context.cpu_cores = navigator.hardwareConcurrency;
    }
  }
  return context;
}

/** Called by the list on a plain row click, before navigation. */
export function markInboxClick(documentId) {
  if (documentId == null) return;
  watchMainThread();
  clicks.set(String(documentId), now());
}

/**
 * Begin timing one document load. Starts from the matching click when
 * there is a recent one, otherwise from now (deep link, refresh, keys).
 *
 * `report: false` keeps the stopwatch but never posts the result: the
 * embedded viewer (framed inside Salesforce, no session) has nobody to
 * attribute the timing to, and a 401 from the report would bounce the
 * frame to the sign-in page.
 */
export function startInboxOpen(
  documentId,
  { viewerCold = false, viewerReadyAt = null, report = true } = {},
) {
  watchMainThread();
  const key = String(documentId);
  const clickedAt = clicks.get(key);
  clicks.delete(key);
  const fromClick = clickedAt != null && now() - clickedAt < CLICK_TTL_MS;
  const startedAt = fromClick ? clickedAt : now();
  const steps = {};
  const context = { viewer_cold: viewerCold, from_click: fromClick };
  let finished = false;

  if (viewerReadyAt != null && viewerReadyAt > startedAt) {
    context.viewer_waited = true;
    steps.viewer_ready = viewerReadyAt - startedAt;
  } else {
    context.viewer_waited = false;
  }
  const hiddenAtStart = typeof document !== "undefined" && document.hidden;

  return {
    documentId,
    get finished() {
      return finished;
    },
    has(step) {
      return step in steps;
    },
    mark(step) {
      if (finished || step in steps) return;
      steps[step] = now() - startedAt;
    },
    note(extra) {
      if (!finished) Object.assign(context, extra);
    },
    finish(outcome) {
      if (finished) return;
      finished = true;
      if (!report) return;
      const endedAt = now();
      const busy = busyBetween(startedAt, endedAt);
      const hidden =
        hiddenAtStart
        || (typeof document !== "undefined" && document.hidden)
        || (lastHiddenAt != null && lastHiddenAt >= startedAt);
      const payload = {
        document_id: Number.isFinite(Number(documentId)) ? Number(documentId) : null,
        outcome,
        total_ms: Math.round(endedAt - startedAt),
        steps: Object.fromEntries(
          Object.entries(steps).map(([name, ms]) => [name, Math.round(ms)]),
        ),
        context: {
          ...connectionContext(),
          ...context,
          hidden,
          long_task_ms: busy.ms,
          long_task_count: busy.count,
          release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
        },
      };
      try {
        Promise.resolve(postInboxOpenTiming(payload)).catch(() => {});
      } catch {
        /* reporting is best-effort */
      }
    },
    cancel() {
      finished = true;
    },
  };
}
